import type { RefObject } from "react";
import {
  SCROLL_DURATION_MS,
  SPRING_DAMPING,
  SPRING_MAX_PX,
  SPRING_MAX_RATIO,
  SPRING_OSCILLATIONS,
  SPRING_START,
} from "./scroll-controller-constants";

interface AnimateScrollTopInput {
  scrollRef: RefObject<HTMLDivElement | null>;
  runIdRef: RefObject<number>;
  isAutoScrollingRef: RefObject<boolean>;
  smoothScroll: boolean;
  targetTop: number;
  instant: boolean;
}

interface ScrollOvershootState {
  limit: number;
  direction: number;
  maxTop: number;
}

interface ScrollRunState {
  scrollRef: RefObject<HTMLDivElement | null>;
  runIdRef: RefObject<number>;
  isAutoScrollingRef: RefObject<boolean>;
  el: HTMLDivElement;
  runId: number;
  clampedTarget: number;
}

interface ScrollAnimationState {
  startTop: number;
  delta: number;
  overshoot: ScrollOvershootState;
  startTime: number;
}

interface ScrollAnimationFrameInput {
  timestamp: number;
  run: ScrollRunState;
  animation: ScrollAnimationState;
}

export function clampScrollTop({
  el,
  targetTop,
}: {
  el: HTMLDivElement;
  targetTop: number;
}): number {
  const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
  return Math.max(0, Math.min(targetTop, maxTop));
}

export function getScrollMaxTop(el: HTMLDivElement): number {
  return Math.max(0, el.scrollHeight - el.clientHeight);
}

export function animateScrollTop({
  scrollRef,
  runIdRef,
  isAutoScrollingRef,
  smoothScroll,
  targetTop,
  instant,
}: AnimateScrollTopInput): void {
  const run = createScrollRunState({
    scrollRef,
    runIdRef,
    isAutoScrollingRef,
    targetTop,
  });
  if (!run) return;

  if (shouldScrollInstantly({ instant, smoothScroll })) {
    finishImmediateScroll({ run });
    return;
  }

  const animation = createScrollAnimationState({ run });
  if (!animation) return;

  scheduleScrollAnimationFrame({ run, animation });
}

function createScrollRunState({
  scrollRef,
  runIdRef,
  isAutoScrollingRef,
  targetTop,
}: Pick<
  AnimateScrollTopInput,
  "scrollRef" | "runIdRef" | "isAutoScrollingRef" | "targetTop"
>): ScrollRunState | null {
  const el = scrollRef.current;
  if (!el) return null;

  runIdRef.current += 1;
  const runId = runIdRef.current;
  isAutoScrollingRef.current = true;

  return {
    scrollRef,
    runIdRef,
    isAutoScrollingRef,
    el,
    runId,
    clampedTarget: clampScrollTop({ el, targetTop }),
  };
}

function shouldScrollInstantly({
  instant,
  smoothScroll,
}: Pick<AnimateScrollTopInput, "instant" | "smoothScroll">): boolean {
  return instant || !smoothScroll;
}

function finishImmediateScroll({ run }: { run: ScrollRunState }): void {
  run.el.scrollTop = run.clampedTarget;
  run.isAutoScrollingRef.current = false;
}

function createScrollAnimationState({
  run,
}: {
  run: ScrollRunState;
}): ScrollAnimationState | null {
  const startTop = run.el.scrollTop;
  const delta = run.clampedTarget - startTop;
  if (Math.abs(delta) < 1) {
    run.isAutoScrollingRef.current = false;
    return null;
  }

  return {
    startTop,
    delta,
    overshoot: getScrollOvershootState({
      el: run.el,
      clampedTarget: run.clampedTarget,
      delta,
    }),
    startTime: nowMs(),
  };
}

function scheduleScrollAnimationFrame({
  run,
  animation,
}: {
  run: ScrollRunState;
  animation: ScrollAnimationState;
}): void {
  requestAnimationFrame((timestamp) => {
    tickScrollAnimation({ timestamp, run, animation });
  });
}

function tickScrollAnimation({
  timestamp,
  run,
  animation,
}: ScrollAnimationFrameInput): void {
  if (isScrollRunCancelled({ run })) return;

  if (!isScrollElementConnected({ run })) {
    run.isAutoScrollingRef.current = false;
    return;
  }

  const progress = getScrollProgress({ timestamp, animation });
  applyScrollAnimationProgress({ progress, run, animation });
  finishOrContinueScrollAnimation({ progress, run, animation });
}

function isScrollRunCancelled({ run }: { run: ScrollRunState }): boolean {
  return run.runId !== run.runIdRef.current;
}

function isScrollElementConnected({ run }: { run: ScrollRunState }): boolean {
  return run.el.isConnected && run.scrollRef.current === run.el;
}

function getScrollProgress({
  timestamp,
  animation,
}: Pick<ScrollAnimationFrameInput, "timestamp" | "animation">): number {
  const elapsed = timestamp - animation.startTime;
  return Math.min(1, elapsed / SCROLL_DURATION_MS);
}

function applyScrollAnimationProgress({
  progress,
  run,
  animation,
}: Pick<ScrollAnimationFrameInput, "run" | "animation"> & {
  progress: number;
}): void {
  if (progress < SPRING_START) {
    run.el.scrollTop =
      animation.startTop +
      animation.delta * easeOutQuint(progress / SPRING_START);
    return;
  }

  run.el.scrollTop = getSpringScrollTop({
    progress,
    clampedTarget: run.clampedTarget,
    overshoot: animation.overshoot,
  });
}

function finishOrContinueScrollAnimation({
  progress,
  run,
  animation,
}: Pick<ScrollAnimationFrameInput, "run" | "animation"> & {
  progress: number;
}): void {
  if (progress < 1) {
    scheduleScrollAnimationFrame({ run, animation });
    return;
  }

  run.el.scrollTop = clampScrollTop({
    el: run.el,
    targetTop: run.clampedTarget,
  });
  run.isAutoScrollingRef.current = false;
}

function easeOutQuint(progress: number): number {
  return 1 - Math.pow(1 - progress, 5);
}

function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function getScrollOvershootState({
  el,
  clampedTarget,
  delta,
}: {
  el: HTMLDivElement;
  clampedTarget: number;
  delta: number;
}): ScrollOvershootState {
  const maxTop = getScrollMaxTop(el);
  const overshootRoom = delta > 0 ? maxTop - clampedTarget : clampedTarget;
  const limit = Math.max(
    0,
    Math.min(SPRING_MAX_PX, Math.abs(delta) * SPRING_MAX_RATIO, overshootRoom),
  );

  return {
    limit,
    direction: delta >= 0 ? 1 : -1,
    maxTop,
  };
}

function getSpringScrollTop({
  progress,
  clampedTarget,
  overshoot,
}: {
  progress: number;
  clampedTarget: number;
  overshoot: ScrollOvershootState;
}): number {
  const springT = (progress - SPRING_START) / (1 - SPRING_START);
  const omega = SPRING_OSCILLATIONS * Math.PI * 2;
  const springOffset =
    overshoot.limit *
    Math.exp(-SPRING_DAMPING * springT) *
    Math.sin(omega * springT);
  const nextTop = clampedTarget + overshoot.direction * springOffset;

  return Math.max(0, Math.min(nextTop, overshoot.maxTop));
}
